import { Card } from "@/components/ui/card"
import { useQuery } from "@tanstack/react-query"
import { supabase } from "@/integrations/supabase/client"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Badge } from "@/components/ui/badge"

const HostOrganizations = ({ profile, session }: { profile: any | null, session: any | null }) => {
  const { data: organizations, isLoading } = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          *,
          corporate_profiles!inner(
            *,
            events(count),
            participants:events(
              event_participants!event_participants_event_id_fkey(count)
            )
          )
        `)
        .eq('user_type', 'company')
        .eq('verification_status', 'approved')

      if (error) throw error
      
      // Transform the data to handle nested structures safely
      return data?.map(profile => {
        const corporateProfile = profile.corporate_profiles
        return {
          ...profile,
          ...corporateProfile,
          eventCount: corporateProfile?.events?.[0]?.count ?? 0,
          participantCount: corporateProfile?.participants?.[0]?.event_participants?.[0]?.count ?? 0
        }
      }) || []
    }
  })

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Organizations</h1>
      
      <Card className="p-6">
        {isLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Loading organizations...</p>
          </div>
        ) : !organizations?.length ? (
          <div className="text-center py-8">
            <p className="text-gray-500">No organizations found</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Organization</TableHead>
                <TableHead>Contact Person</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Total Events</TableHead>
                <TableHead>Total Participants</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizations.map((org) => (
                <TableRow key={org.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
                        {org.company_name?.substring(0, 2).toUpperCase() || 'CO'}
                      </div>
                      <div>
                        <div className="font-medium">{org.company_name}</div>
                        <div className="text-sm text-gray-500">{org.company_email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium">{`${org.first_name} ${org.last_name}`}</div>
                      <div className="text-sm text-gray-500">{org.title}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div>{`${org.isd_code} ${org.phone}`}</div>
                      <div className="text-sm text-gray-500">{org.company_email}</div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="rounded-full">
                      {org.eventCount} Events
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="rounded-full">
                      {org.participantCount} Participants
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  )
}

export default HostOrganizations